import React from 'react';

// Where the money is for the selected period (Apal 2026-10-11: "one side
// showing selection, the other side showing allocation in pie chart" · "this
// is a critical info"). Invested indexes get a slice and a row with their %;
// selected indexes that take 0% are listed underneath with the reason.
const COLORS = ['#2dd4bf', '#38bdf8', '#818cf8', '#c084fc', '#f472b6', '#fb923c', '#facc15', '#a3e635',
  '#34d399', '#60a5fa', '#a78bfa', '#e879f9', '#fb7185', '#fdba74', '#fde047', '#bef264', '#5eead4', '#93c5fd'];

const arc = (cx, cy, r, a0, a1) => {
  const p = (a) => [cx + r * Math.sin(a), cy - r * Math.cos(a)];
  const [x0, y0] = p(a0); const [x1, y1] = p(a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1} ${y1}`;
};

const fmt = (w) => (Math.abs(w - Math.round(w)) < 0.05 ? String(Math.round(w)) : w.toFixed(1));

const yearsOf = (st) => {
  const a = (st.from || '').slice(0, 4), b = (st.to || '').slice(0, 4);
  return a === b ? a : `${a}–${b}`;
};

function StretchRows({ stretches, colorOf }) {
  return (
    <div className="space-y-3" data-testid="signal-allocation-stretches">
      {stretches.map(st => {
        const each = st.members.length > 1 && st.members.every(m => fmt(m.weight) === fmt(st.members[0].weight));
        return (
          <div key={st.from} data-testid={`signal-allocation-stretch-${yearsOf(st)}`}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className="font-semibold text-white tabular-nums">{yearsOf(st)}</span>
              <span className="text-dark-300">
                {st.members.length} {st.members.length === 1 ? 'index' : 'indexes'}
                {each ? <> · <span className="font-bold text-white">{fmt(st.members[0].weight)}%</span> each</> : null}
              </span>
            </div>
            <div className="flex h-3 w-full overflow-hidden rounded-sm bg-dark-700">
              {st.members.map(m => (
                <div key={m.symbol} style={{ width: `${m.weight}%`, background: colorOf(m.symbol) }}
                  title={`${m.label} ${fmt(m.weight)}%`} />
              ))}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-dark-300">
              {st.members.map(m => (
                <span key={m.symbol} className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm" style={{ background: colorOf(m.symbol) }} />
                  {m.label} <span className="font-semibold text-white tabular-nums">{fmt(m.weight)}%</span>
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AllocationDonut({ members, zero, segments = null }) {
  const size = 132, cx = size / 2, cy = size / 2, r = 50, stroke = 22;
  // A range across years can run on different splits (India joins in 2006,
  // crypto in 2017): consecutive years on the same split form one stretch.
  const stretches = [];
  (segments || []).forEach(sg => {
    const key = sg.members.map(m => `${m.symbol}:${fmt(m.weight)}`).join('|');
    const last = stretches[stretches.length - 1];
    if (last && last.key === key) last.to = sg.to;
    else stretches.push({ key, from: sg.from, to: sg.to, members: sg.members });
  });
  // one colour per index across the donut and every stretch
  const order = [];
  [...stretches.flatMap(st => st.members), ...members].forEach(m => { if (!order.includes(m.symbol)) order.push(m.symbol); });
  const colorOf = (sym) => COLORS[order.indexOf(sym) % COLORS.length];
  let a = 0;
  const slices = members.map((m) => {
    const span = (m.weight / 100) * 2 * Math.PI;
    const s = { ...m, color: colorOf(m.symbol), a0: a, a1: a + span };
    a += span;
    return s;
  });
  const each = members.length > 1 && members.every(m => fmt(m.weight) === fmt(members[0].weight));
  return (
    <div data-testid="signal-allocation-panel">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs text-dark-400 uppercase tracking-wider font-semibold">Allocation</span>
        {stretches.length > 1 && <span className="text-[11px] text-dark-500">changes as indexes become available</span>}
      </div>
      {stretches.length > 1 ? (
        <StretchRows stretches={stretches} colorOf={colorOf} />
      ) : members.length === 0 ? (
        <p className="text-xs text-dark-500">Nothing invested in this period.</p>
      ) : (
        <div className="flex items-start gap-4">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" role="img"
            aria-label={members.map(m => `${m.label} ${fmt(m.weight)}%`).join(', ')}>
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1e293b" strokeWidth={stroke} />
            {slices.length === 1
              ? <circle cx={cx} cy={cy} r={r} fill="none" stroke={slices[0].color} strokeWidth={stroke} />
              : slices.map(s => (
                <path key={s.symbol} d={arc(cx, cy, r, s.a0, s.a1 - 0.012)} fill="none" stroke={s.color} strokeWidth={stroke} />
              ))}
            <text x={cx} y={cy - 2} textAnchor="middle" className="fill-white" style={{ fontSize: 20, fontWeight: 700 }}>
              {each || members.length === 1 ? `${fmt(members[0].weight)}%` : members.length}
            </text>
            <text x={cx} y={cy + 15} textAnchor="middle" style={{ fontSize: 10, fill: '#94a3b8' }}>
              {members.length === 1 ? 'in one index' : each ? `each · ${members.length} indexes` : 'indexes'}
            </text>
          </svg>
          <ul className="min-w-0 flex-1 space-y-1" data-testid="signal-allocation-list">
            {slices.map(s => (
              <li key={s.symbol} className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="truncate text-dark-200">{s.label}</span>
                <span className="ml-auto font-bold tabular-nums text-white">{fmt(s.weight)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {zero.length > 0 && (
        <div className="mt-3 pt-2 border-t border-dark-700 space-y-1" data-testid="signal-allocation-zero-list">
          {zero.map(z => (
            <div key={z.reason} className="text-xs text-dark-400">
              <span className="font-semibold text-dark-300">0%</span> — {z.reason}: {z.labels.join(' · ')}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
